import { isCancelledError, queryOptions, type QueryClient } from '@tanstack/react-query';

import { getMe, getSession, type Membership, type SessionState } from '@/shared/api/auth';
import { ApiError } from '@/shared/api/http';
import { setActiveTimeZone } from '@/shared/format';
import { forgetLists } from '@/shared/navigation/listMemory';
import { setActiveFirstDayOfWeek } from '@/shared/week';

export const sessionQueryKey = ['auth', 'session'] as const;
export const meQueryKey = ['auth', 'me'] as const;

/**
 * Bumped by {@link forgetSession}: a session read that was already in flight when the session was
 * forgotten (a tenant change, a logout) answers for the tenant that is gone, and its zone must not
 * overwrite the one of whoever comes next.
 */
let sessionGeneration = 0;

/**
 * `AM-879`: set by {@link leaveSignedOut} while "Cerrar sesión" takes the person to the sign-in screen. The API has
 * just closed the session, so the guard of that screen knows the answer without asking: asking left a refused read
 * (401) in the browser's console.
 */
let leaving = false;

/** Whether "Cerrar sesión" is in progress: its refused reads (401) are expected, not an expiry. */
export function isLeavingSession(): boolean {
  return leaving;
}

/**
 * The session is the SPA's bootstrap (07 §4). A 401 is a normal answer here ("not signed in"),
 * so it is never retried; anything else is a real failure.
 */
export function sessionQueryOptions() {
  return queryOptions({
    queryKey: sessionQueryKey,
    // No abort signal: every route guard awaits this read, and a lazily loaded page that suspends
    // unmounts whatever observed it mid-flight (`AM-197`); an aborted read would reach the guard as
    // a failure and show the error page instead of the screen.
    queryFn: async () => {
      const generation = sessionGeneration;
      const state = await getSession();
      if (generation === sessionGeneration) {
        applyTenantCalendar(state);
      }
      return state;
    },
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Every date and hour of the console, the portal and the platform console inside a tenant is shown
 * in the zone of the tenant the session is in (§8.1), and "today" is the day in that zone: the
 * session says which one it is (`activeTenant.timeZone`) and this hands it to the format helpers,
 * which read it by default (`setActiveTimeZone`). A session with no active tenant (the sign-in
 * screens, the platform console before it enters one) has none, and the platform's own zone stands
 * in. Runs wherever a session state is read, before any page of it renders.
 *
 * `AM-361`: the session also says which day the tenant's week starts on
 * (`activeTenant.firstDayOfWeek`), and this hands it to the calendars and to every "esta semana"
 * (`setActiveFirstDayOfWeek`); without a tenant, Monday.
 */
export function applyTenantCalendar(state: SessionState | null): void {
  // A live session that brings another zone than the one in force repaints the screens already
  // mounted (the platform changed the tenant's while the session was open, `AM-282`); one that
  // goes away only puts the default back.
  setActiveTimeZone(state?.activeTenant?.timeZone, { repaint: state?.activeTenant != null });
  // The calendars follow a new first day by themselves (`TenantDatesProvider` listens to it), and
  // a "esta semana" is computed when its screen asks: nothing to repaint.
  setActiveFirstDayOfWeek(state?.activeTenant?.firstDayOfWeek);
}

export function meQueryOptions() {
  return queryOptions({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => getMe(signal),
    retry: false,
    staleTime: 60_000,
  });
}

/** The cached session, fetched once per navigation; null when the API says there is none. */
export async function loadSession(
  queryClient: QueryClient,
  attempts = 2,
): Promise<SessionState | null> {
  if (leaving) {
    return null;
  }

  try {
    return await queryClient.ensureQueryData(sessionQueryOptions());
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      queryClient.removeQueries({ queryKey: sessionQueryKey });
      applyTenantCalendar(null);
      return null;
    }

    // A read cancelled by somebody else is not an answer: ask again (`AM-197`).
    if (isCancelledError(error) && attempts > 1) {
      return loadSession(queryClient, attempts - 1);
    }

    throw error;
  }
}

/** Memberships the person can enter: the API auto-activates a single one, so this drives `/login/tenant`. */
export function selectableMemberships(state: SessionState): Membership[] {
  return state.memberships.filter((membership) => membership.status === 'active');
}

/**
 * Whether this session may enter `/platform` (ISO-24, `AM-134` point 11): the platform block's own
 * mark, from a live row of `platform_admins`. `identity.isPlatformAdmin` only backs it up for a
 * session whose `platform` block did not arrive - the contract keeps it always present, so this is
 * only a rollout safety net, never the steady state.
 */
export function isPlatformAdminSession(state: SessionState): boolean {
  return state.platform?.isPlatformAdmin ?? state.identity.isPlatformAdmin;
}

/**
 * Forgets everything the previous person could see (§2.5: a tenant change reloads, a logout
 * clears). Beyond the query cache, this also clears what the PWA shell may have kept around
 * (`AM-132 point 13`, ISO-18 c): browser storage and the service worker's own caches, each wrapped
 * so a private window, a blocked API or the absence of a service worker never breaks sign-out.
 */
export function forgetSession(queryClient: QueryClient): void {
  queryClient.clear();
  // The next person, or the next tenant, brings its own zone with its own session.
  sessionGeneration += 1;
  // ...and its own lists: the way back to a list carries nothing of whoever was here before.
  forgetLists();
  applyTenantCalendar(null);

  try {
    window.localStorage.clear();
  } catch {
    // A private window, or storage the browser blocked outright, leaves nothing to clear anyway.
  }

  try {
    window.sessionStorage.clear();
  } catch {
    // Same as above: nothing to do when the browser never granted storage in the first place.
  }

  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((names) => Promise.all(names.map((name) => caches.delete(name))))
      .catch(() => {
        // No service worker registered, or the Cache Storage API is unavailable here: the session
        // itself is still gone, which is what matters (ISO-18 c).
      });
  }
}

/**
 * `AM-879`: what "Cerrar sesión" does once the API closed the session: it leaves the screen first and forgets after. The
 * screen still mounted (the shell, the portal, the platform console) read the session, the person and the tenant's
 * capabilities again the moment the cache was cleared under it, and the sign-in screen asked for the session the API
 * had just closed: three refused reads (401) in the browser's console. `go` is the navigation to the sign-in screen.
 */
export async function leaveSignedOut(
  queryClient: QueryClient,
  go: () => Promise<unknown>,
): Promise<void> {
  leaving = true;
  try {
    await queryClient.cancelQueries();
    await go();
  } finally {
    leaving = false;
    forgetSession(queryClient);
  }
}
